import { gql } from '@apollo/client';
import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';
import TechnologyPageComponent from '../../components/elements/TechnologyPage';
import { RelatedProject } from '../../components/elements/TechnologyPage/template';
import PageLayout, { MenuProps } from '../../layouts/Page';
import { GET_SINGLE_TECHNOLOGY } from '../../queries/technology';
import { Technology } from '../../types/schema';
import { initializeApollo } from '../../utils/apollo';
import { Error, sendError } from '../../utils/error';
import { flatten } from '../../utils/object';

type TechnologiesProps = {
  technology: Technology;
  menus: MenuProps;
  relatedProjects: RelatedProject[];
};

const Technologies: React.FC<TechnologiesProps> = ({ technology, menus, relatedProjects }) => {
  return (
    <PageLayout menus={menus} locale="en">
      <TechnologyPageComponent
        language="en"
        title={technology.title as string}
        html={technology.body as string}
        image={(technology.logo as unknown as { url?: string })?.url as string}
        slug={technology.slug as string}
        relatedProjects={relatedProjects}
      />
    </PageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const apolloClient = initializeApollo();
  const { data } = await apolloClient.query({
    query: gql`
      query GET_TECHNOLOGIES {
        technologies(locale: "en") {
          slug
          locale
        }
      }
    `,
  });

  const newData = flatten(data);

  const paths = (newData as { technologies: Technology[] }).technologies
    .filter((tech) => !!tech.slug)
    .map((tech: Technology) => {
      return {
        params: { slug: tech.slug as string },
        locale: 'en',
      };
    });
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { slug } = ctx.params as { slug: string };
  const { locale } = ctx;

  try {
    const apolloClient = initializeApollo();
    const { data } = await apolloClient.query({
      query: GET_SINGLE_TECHNOLOGY,
      variables: { slug, locale },
    });

    const newData = flatten(data);

    const technology = (newData as { allTechnologies: Technology[] })?.allTechnologies?.[0];

    if (locale !== technology?.locale) {
      return {
        notFound: true,
      };
    }

    type RawProject = {
      documentId: string;
      title: string;
      slug: string;
      locale: string;
      projectsDynamic: { technologies?: { slug: string }[] }[];
    };

    const allProjects: RawProject[] = (newData as { allProjects: RawProject[] }).allProjects ?? [];
    const relatedProjects = allProjects.filter(
      (project) =>
        project.projectsDynamic?.some(
          (block) => block.technologies?.some((tech) => tech.slug === slug),
        ),
    );

    return {
      props: {
        technology,
        relatedProjects,
        menus: {
          header: newData.header,
          technologies: newData.technologies,
          services: newData.services,
          footerPt: newData.footerPt,
          footerEn: newData.footerEn,
        },
      },
    };
  } catch (error) {
    sendError({ error: error as Error, slug, message: 'Error on technologies/[slug] page' });
    return {
      notFound: true,
    };
  }
};

export default Technologies;
